import React, { useState, useEffect, forwardRef } from 'react';
import {
  datasetTypeService,
  datasetGroupService,
  datasetSubtypeService,
  datasetService,
} from '../services';
import {
  Checkbox,
  CustomPropertiesForm,
  FormTabs,
  Input,
  Select,
  SelectOwner,
  InputStatus,
  SelectButtons,
  formatSelectOptions,
  Textarea,
  textareaInitialState,
  textareaToRaw,
} from '../components';
import {
  getBatchEditValue,
  Asset,
  DatasetSubtypeOperation,
  formatDatasetSegment,
  getHierarchyPath,
} from '../helpers';
import { useCustomProperties, useDescriptionSource, useGlobalState, useMessage } from '../hooks';

const _AddDatasetForm = ({ mode, data, submitForm, loading, setLoading, formErrors = {} }, ref) => {
  const initialFormState = {
    dataset_id: null,
    dataset_name: '',
    dataset_description: textareaInitialState({}),
    dataset_source_description: textareaInitialState({}),
    dataset_source_description_secondary: textareaInitialState({}),
    dataset_description_source: 'k',
    dataset_type_id: '',
    dataset_group_id: '',
    status_id: '',
    owner_principal_id: '',
    dataset_subtype_id: '',
    parent_dataset_id: null,
  };

  const initialBatchEditItems = {
    batch_dataset_type_id: false,
    batch_dataset_group_id: false,
    batch_status_id: false,
    batch_owner_principal_id: false,
    batch_dataset_description_source: false,
    batch_dataset_subtype_id: false,
    batch_parent_dataset_id: false,
  };

  const [loggedInUser] = useGlobalState('loggedInUser');
  const [currentDatasetGroup] = useGlobalState('currentDatasetGroup');
  const [statuses] = useGlobalState('statuses');
  const [formData, setFormData] = useState(initialFormState);
  const [batchEdit, setBatchEdit] = useState(false);
  const [batchEditItems, setBatchEditItems] = useState(initialBatchEditItems);
  const [datasetTypes, setDatasetTypes] = useState([]);
  const [datasetGroups, setDatasetGroups] = useState([]);
  const [datasetSubtypes, setDatasetSubtypes] = useState([]);
  const [parentDatasets, setParentDatasets] = useState([]);
  const [defaultSelectedTab, setDefaultSelectedTab] = useState();

  const { showMessage, hideMessage } = useMessage();
  const { getDescription, getDescriptionSourceOptions } = useDescriptionSource({
    data,
    formData,
    objectName: 'dataset',
  });
  const {
    customProperties,
    formCustomPropertyValues,
    batchEditFormCustomPropertyValues,
    batchEditItemsFormCustomPropertyValues,
    setFormCustomPropertyValues,
    hasCustomProperties,
    getCustomPropertyValuesForSubmit,
    resetFormCustomPropertyValues,
  } = useCustomProperties({
    objectName: Asset.Dataset,
    // In add_child, data[0] is the PARENT dataset, so its custom property values must not be loaded —
    // the child would open pre-filled with values belonging to its parent. Every other mode does read
    // them: editing shows the dataset's own values, and adding with a dataset selected copies it.
    objectIds: mode !== 'add_child' ? data.map(x => x.dataset_id) : [],
  });

  // When editing, use the dataset's own datasource; when creating (no `data`, or in views like
  // User where it's not on the row) fall back to the dataset group currently in context.
  let datasource_id = data[0]?.datasource_id || currentDatasetGroup?.datasource_id;

  // A subtype belongs to exactly one dataset type -- an HTTP method to an API endpoint, a kind to a
  // Power BI item -- so the picker is gated on the chosen type, not on the datasource.
  const selectedDatasetType = datasetTypes.find(
    t => String(t.dataset_type_id) === String(formData.dataset_type_id)
  );
  const isApiEndpointType = selectedDatasetType?.dataset_type_code === 'api_endpoint';

  // The subtypes the chosen type defines, in the order the API returned them (by sort order).
  const subtypesForType = datasetSubtypes.filter(
    x => String(x.dataset_type_id) === String(formData.dataset_type_id)
  );

  // Only worth a picker when there is something to choose between. A Power BI kind is the only
  // subtype of its type, so it is filled in for the user rather than asked for.
  const hasSubtypeChoice = subtypesForType.length > 1;

  // Only datasets whose type supports nesting (dataset_type.has_children) can be parents.
  // The flag is not on the dataset rows, so cross-reference the fetched dataset types by id.
  // Derived in render (not in the fetch) so it stays correct regardless of which loads first.
  const nestableParentDatasets = parentDatasets.filter(
    x => datasetTypes.find(t => t.dataset_type_id === x.dataset_type_id)?.has_children
  );

  useEffect(() => {
    fetchDatasetTypes();
    fetchDatasetGroups();
    fetchDatasetSubtypes();
  }, []);

  useEffect(() => {
    if (formData.dataset_group_id) {
      fetchParentDatasets(formData.dataset_group_id);
    }
  }, [formData.dataset_group_id]);

  // Keep dataset_subtype_id and parent_dataset_id consistent with the selected dataset type
  // (single-edit only):
  //  - set dataset_subtype_id to the type's first subtype when none is set, so the value the
  //    backend requires is always present: for an API endpoint that is GET, for a Power BI item it
  //    is the only subtype the type has;
  //  - clear dataset_subtype_id when the chosen type has no subtypes, so a dataset isn't saved
  //    with one left over from a type it no longer has;
  //  - clear dataset_subtype_id when the one held belongs to another type, which is what a change
  //    of type leaves behind;
  //  - clear parent_dataset_id when the type is "API Endpoint", since an endpoint is the
  //    root of its hierarchy and cannot be nested (the parent picker is hidden for it).
  // Skipped in batch mode and until the dataset types and subtypes have loaded (avoids wiping an
  // existing value before the type's subtypes can be resolved).
  useEffect(() => {
    if (batchEdit || datasetTypes.length === 0 || datasetSubtypes.length === 0) return;
    if (!formData.dataset_type_id) return;

    if (subtypesForType.length === 0) {
      if (formData.dataset_subtype_id) {
        setFormData(prev => ({ ...prev, dataset_subtype_id: '' }));
      }
    } else {
      const isForThisType = subtypesForType.some(
        x => String(x.dataset_subtype_id) === String(formData.dataset_subtype_id)
      );

      if (!isForThisType) {
        setFormData(prev => ({
          ...prev,
          dataset_subtype_id: subtypesForType[0].dataset_subtype_id,
        }));
      }
    }

    if (isApiEndpointType && formData.parent_dataset_id != null) {
      setFormData(prev => ({ ...prev, parent_dataset_id: null }));
    }
  }, [
    isApiEndpointType,
    datasetSubtypes,
    datasetTypes.length,
    formData.dataset_type_id,
    batchEdit,
  ]);

  useEffect(() => {
    setBatchEdit(false);
    if (data.length === 1 && mode !== 'add_child') {
      setFormData(prev => {
        return {
          ...prev,
          dataset_id: data[0].dataset_id,
          dataset_name: data[0].dataset_name || '',
          dataset_description: textareaInitialState({ value: data[0].dataset_description || '' }),
          dataset_source_description: textareaInitialState({
            value: data[0].dataset_source_description || '',
          }),
          dataset_source_description_secondary: textareaInitialState({
            value: data[0].dataset_source_description_secondary || '',
          }),
          dataset_description_source:
            data[0].dataset_description_source || prev.dataset_description_source,
          dataset_type_id: data[0].dataset_type_id,
          dataset_group_id: data[0].dataset_group_id,
          status_id: data[0].status_id,
          owner_principal_id: data[0].owner_principal_id,
          dataset_subtype_id: data[0].dataset_subtype_id || '',
          parent_dataset_id: data[0].parent_dataset_id || null,
        };
      });
    } else if (data.length > 1 && mode !== 'add') {
      setBatchEdit(true);

      setFormData({
        dataset_type_id: getBatchEditValue(data, 'dataset_type_id'),
        dataset_group_id: getBatchEditValue(data, 'dataset_group_id'),
        status_id: getBatchEditValue(data, 'status_id'),
        owner_principal_id: getBatchEditValue(data, 'owner_principal_id'),
        dataset_description_source: getBatchEditValue(data, 'dataset_description_source'),
        dataset_subtype_id: getBatchEditValue(data, 'dataset_subtype_id'),
        parent_dataset_id: getBatchEditValue(data, 'parent_dataset_id'),
      });
    } else if (mode === 'add_child') {
      setFormData({
        ...formData,
        status_id: statuses.default_system_status_id,
        owner_principal_id: loggedInUser?.principal_id,
        dataset_group_id: data[0].dataset_group_id || currentDatasetGroup?.dataset_group_id,
        parent_dataset_id: data[0].dataset_id,
      });
    } else if (mode === 'add') {
      setFormData({
        ...formData,
        status_id: statuses.default_system_status_id,
        owner_principal_id: loggedInUser?.principal_id,
        dataset_group_id: currentDatasetGroup.dataset_group_id,
      });
    } else {
      resetForm();
    }
  }, [mode, data, loggedInUser, currentDatasetGroup]);

  const fetchDatasetTypes = async () => {
    datasetTypeService
      .getAll()
      .then(res => {
        res && setDatasetTypes(res.dataset_types);

        if (mode === 'add_child') {
          console.log(data);
          let datasetTypeId;
          switch (data[0].datasource_type_code) {
            case 'api':
              datasetTypeId = res.dataset_types.find(
                x => x.dataset_type_code === 'schema'
              )?.dataset_type_id;
              break;
          }
          setFormData(prev => ({
            ...prev,
            dataset_type_id: datasetTypeId,
          }));
        }
      })
      .catch(err => {
        showMessage(err, 'error');
      });
  };

  const fetchDatasetGroups = async () => {
    datasetGroupService
      .getByDatasourceId(datasource_id)
      .then(res => {
        res && setDatasetGroups(res.dataset_groups);
      })
      .catch(err => {
        showMessage(err, 'error');
      });
  };

  const fetchDatasetSubtypes = async () => {
    datasetSubtypeService
      .getAll()
      .then(res => {
        res &&
          setDatasetSubtypes(
            res.dataset_subtypes.map(method => ({
              ...method,
              className:
                DatasetSubtypeOperation[method.dataset_subtype_operation]?.className + ' width-50',
              tooltip: method.dataset_subtype_code.toUpperCase(),
            }))
          );
      })
      .catch(err => {
        showMessage(err, 'error');
      });
  };

  /**
   * Fetches datasets in the given group to populate the parent dataset select.
   * Excludes the current dataset (in edit mode) to prevent self-referencing.
   * @param {number} datasetGroupId - The dataset_group_id to fetch datasets for.
   */
  const fetchParentDatasets = async datasetGroupId => {
    datasetService
      .getByDatasetGroupId(datasetGroupId)
      .then(res => {
        if (res) {
          let result = res.datasets;

          if (data.length > 0 && mode !== 'add' && mode !== 'add_child') {
            result = result.filter(x => !data.find(y => y.dataset_id === x.dataset_id));
          }

          setParentDatasets(
            result.map(x => ({
              ...x,
              dataset_path: getHierarchyPath(x, res.datasets, Asset.Dataset),
            }))
          );
        }
      })
      .catch(err => {
        showMessage(err, 'error');
      });
  };

  const handleInputChange = (event, formPart) => {
    let { name, value } = '';

    // Handle standard form inputs
    if (event.target !== undefined) {
      name = event.target.name;
      value = event.target.value;

      // If input element is a checkbox, we cannot use "value"
      if (event.target.type === 'checkbox') {
        value = event.target.checked;
      }

      // Handle custom form inputs
    } else {
      name = event.name;
      value = event.value;
    }

    switch (formPart) {
      case 'batch':
        setBatchEditItems({ ...batchEditItems, [name]: value });
        break;
      default:
        setFormData({ ...formData, [name]: value });
    }

    // Hide errors on input change
    if (formErrors && formErrors[name]) {
      delete formErrors[name];
      hideMessage();
    }
  };

  const handleClickOnDescription = () => {
    if (formData.dataset_description_source !== 'k') {
      setFormData(prevFormData => {
        return {
          ...prevFormData,
          dataset_description_source: 'k',
        };
      });
    }
  };

  const onSubmit = event => {
    event.preventDefault();

    setLoading(true);

    // Form validation
    //if (!formData.user_fullname || !formData.user_username) return

    let dataToSubmit;

    if (batchEdit) {
      let key = '';
      let tmp = {};
      const objects = Object.entries(batchEditItems);

      for (let i = 0; i < objects.length; i++) {
        if (objects[i][1]) {
          key = objects[i][0].replace('batch_', '');
          tmp = { ...tmp, [key]: formData[key] };
        }
      }

      dataToSubmit = data.map(item => {
        return {
          dataset_id: item.dataset_id,
          dataset_name: item.dataset_name,
          dataset_type_id: item.dataset_type_id,
          dataset_group_id: item.dataset_group_id,
          status_id: item.status_id,
          owner_principal_id: item.owner_principal_id,
          dataset_description_source: item.dataset_description_source,
          ...tmp,
          ...getCustomPropertyValuesForSubmit(item.dataset_id),
        };
      });
    } else {
      dataToSubmit = {
        ...formData,
        ...getCustomPropertyValuesForSubmit(),
      };

      if (dataToSubmit.dataset_description_source !== 'k') {
        delete dataToSubmit.dataset_description;
      } else {
        dataToSubmit.dataset_description = textareaToRaw({ value: formData.dataset_description });
      }

      delete dataToSubmit.dataset_source_description;
      delete dataToSubmit.dataset_source_description_secondary;
    }

    // submit form
    submitForm(dataToSubmit)
      .then(() => resetForm())
      .catch(() => {
        // Errors handled in submitForm function
      })
      .finally(() => setLoading(false));
  };

  const resetForm = () => {
    resetFormCustomPropertyValues();
    setBatchEdit(false);
    setBatchEditItems(initialBatchEditItems);
    setDefaultSelectedTab({ tab: 'general' });
    setFormData(() => {
      let tmp = {
        ...initialFormState,
        status_id: statuses.default_system_status_id,
        owner_principal_id: loggedInUser ? loggedInUser.principal_id : null,
        dataset_group_id: currentDatasetGroup ? currentDatasetGroup.dataset_group_id : null,
        parent_dataset_id: null,
      };

      if (mode === 'add_child') {
        tmp = {
          ...tmp,
          parent_dataset_id: data[0].dataset_id,
          dataset_type_id:
            datasetTypes &&
            datasetTypes.find(x => x.dataset_type_code === 'schema')?.dataset_type_id,
        };
      }

      return tmp;
    });
  };

  return (
    <div className="form">
      <form onSubmit={onSubmit} ref={ref}>
        <div className="form-block vertical">
          <InputStatus
            name="dataset_name"
            value={formData.dataset_name}
            statusValue={formData.status_id}
            assetCategory="system"
            onChange={handleInputChange}
            loading={loading}
            batchEdit={batchEdit}
            batchEditItems={batchEditItems}
            validation={
              formErrors.dataset_name
                ? { valid: false, message: formErrors.dataset_name }
                : undefined
            }
          />
        </div>

        <div className="form-block vertical">
          {batchEdit && (
            <label>
              <Checkbox
                checked={batchEditItems.batch_dataset_type_id}
                name="batch_dataset_type_id"
                label="Edit Dataset Type"
                onChange={e => handleInputChange(e, 'batch')}
                disabled={loading}
              />
            </label>
          )}
          <Select
            name="dataset_type_id"
            label={!batchEdit}
            value={formData.dataset_type_id === 'multiple' ? undefined : formData.dataset_type_id}
            options={formatSelectOptions({
              options: datasetTypes,
              optionValue: 'dataset_type_id',
              optionLabel: 'dataset_type_name',
              optionIcon: 'dataset_type_name',
            })}
            onChange={handleInputChange}
            placeholder={
              formData.dataset_type_id === 'multiple' ? '< Multiple >' : 'Select dataset type...'
            }
            disabled={loading || (batchEdit && !batchEditItems.batch_dataset_type_id)}
            validation={
              formErrors.dataset_type_id
                ? { valid: false, message: formErrors.dataset_type_id }
                : undefined
            }
            // Where the chosen type offers a choice of subtype, attach its picker inline (combined
            // box). Only in single-edit mode — batch edit keeps a separate block below
            // so it can carry its own "Edit Subtype" checkbox.
            withSelect={!batchEdit && hasSubtypeChoice}
            selectProps={[
              {
                name: 'dataset_subtype_id',
                label: 'Subtype',
                tooltip:
                  'Select the subtype for this dataset, e.g. the HTTP method of an API endpoint',
                options: formatSelectOptions({
                  options: subtypesForType,
                  optionValue: 'dataset_subtype_id',
                  optionLabel: 'dataset_subtype_name',
                  tooltip: 'tooltip',
                  className: 'className',
                }),
                isMulti: false,
                value: formData.dataset_subtype_id,
                onChange: handleInputChange,
              },
            ]}
          />
        </div>

        {batchEdit && hasSubtypeChoice && (
          <>
            <div className="form-block vertical">
              {batchEdit && (
                <label>
                  <Checkbox
                    checked={batchEditItems.batch_dataset_subtype_id}
                    name="batch_dataset_subtype_id"
                    label="Edit Subtype"
                    onChange={e => handleInputChange(e, 'batch')}
                    disabled={loading}
                  />
                </label>
              )}
              <Select
                name="dataset_subtype_id"
                label={!batchEdit}
                value={
                  formData.dataset_subtype_id === 'multiple'
                    ? undefined
                    : formData.dataset_subtype_id
                }
                options={formatSelectOptions({
                  options: subtypesForType,
                  optionValue: 'dataset_subtype_id',
                  optionLabel: 'dataset_subtype_name',
                  tooltip: 'tooltip',
                  className: 'className',
                })}
                sortOptions={false}
                onChange={handleInputChange}
                placeholder={
                  formData.dataset_subtype_id === 'multiple' ? '< Multiple >' : 'Select subtype...'
                }
                disabled={loading || (batchEdit && !batchEditItems.batch_dataset_subtype_id)}
                validation={
                  formErrors.dataset_subtype_id
                    ? { valid: false, message: formErrors.dataset_subtype_id }
                    : undefined
                }
              />
            </div>
          </>
        )}

        <FormTabs
          defaultSelectedTab={defaultSelectedTab}
          formErrors={formErrors}
          hideTabsIfOnlyOne={true}
        >
          <div label="General">
            {!batchEdit && (
              <div className="form-block vertical">
                <Textarea
                  name="dataset_description"
                  value={getDescription()}
                  onChange={handleInputChange}
                  onClick={handleClickOnDescription}
                  disabled={loading || formData.dataset_description_source !== 'k'}
                  title={
                    formData.dataset_description_source !== 'k'
                      ? `The description is automatically synced from the datasource`
                      : ''
                  }
                  isOptional={true}
                />
              </div>
            )}

            <div className="form-block vertical">
              {batchEdit && (
                <label>
                  <Checkbox
                    checked={batchEditItems.batch_dataset_description_source}
                    name="batch_dataset_description_source"
                    label="Edit Description Source"
                    onChange={e => handleInputChange(e, 'batch')}
                    disabled={loading}
                  />
                </label>
              )}

              <SelectButtons
                name="dataset_description_source"
                value={
                  formData.dataset_description_source === 'multiple'
                    ? undefined
                    : formData.dataset_description_source
                }
                options={getDescriptionSourceOptions()}
                onChange={handleInputChange}
                disabled={
                  loading || (batchEdit && !batchEditItems.batch_dataset_description_source)
                }
                title="Select whether to use a custom description set here in Katalogue or automatically sync the description from the datasource."
              />
            </div>

            <div className="form-block vertical">
              {batchEdit && (
                <label>
                  <Checkbox
                    checked={batchEditItems.batch_dataset_group_id}
                    name="batch_dataset_group_id"
                    label="Edit Dataset Group"
                    onChange={e => handleInputChange(e, 'batch')}
                    disabled={loading}
                  />
                </label>
              )}
              <Select
                name="dataset_group_id"
                label={!batchEdit}
                value={
                  formData.dataset_group_id === 'multiple' ? undefined : formData.dataset_group_id
                }
                options={formatSelectOptions({
                  options: datasetGroups,
                  optionValue: 'dataset_group_id',
                  optionLabel: 'dataset_group_name',
                  tooltip: 'dataset_group_description',
                })}
                onChange={handleInputChange}
                placeholder={formData.owner_principal_id === 'multiple' ? '< Multiple >' : ''}
                disabled={loading || (batchEdit && !batchEditItems.batch_dataset_group_id)}
              />
            </div>

            {/* Hide the parent dataset control entirely when the group has no datasets whose
                type supports nesting, or when the selected type is an API endpoint (an
                endpoint is the root of its hierarchy and cannot itself be nested). */}
            {!isApiEndpointType && nestableParentDatasets.length > 0 && (
              <div className="form-block vertical">
                {batchEdit && (
                  <label>
                    <Checkbox
                      checked={batchEditItems.batch_parent_dataset_id}
                      name="batch_parent_dataset_id"
                      label="Edit Parent Dataset"
                      onChange={e => handleInputChange(e, 'batch')}
                      disabled={loading}
                    />
                  </label>
                )}
                <Select
                  name="parent_dataset_id"
                  label={!batchEdit}
                  title="Assign this dataset as a child of another dataset in the same group. Used to model nested structures, e.g. an API endpoint with its request and response as child datasets."
                  value={
                    formData.parent_dataset_id === 'multiple'
                      ? undefined
                      : formData.parent_dataset_id
                  }
                  options={formatSelectOptions({
                    options: nestableParentDatasets.map(x => ({
                      ...x,
                      // Label shows the API method as a pill + the dataset name (JSX); the
                      // dataset-type icon stays in the icon slot, and searchText is the plain-text
                      // mirror so typeahead still matches.
                      dataset_label: formatDatasetSegment(x.dataset_name, x.dataset_subtype_name, {
                        datasetSubtypeOperation: x.dataset_subtype_operation,
                        asPill: true,
                      }),
                      searchText: formatDatasetSegment(x.dataset_name, x.dataset_subtype_name),
                    })),
                    optionValue: 'dataset_id',
                    optionLabel: 'dataset_label',
                    optionIcon: 'dataset_type_name',
                    tooltip: 'dataset_description',
                  })}
                  onChange={handleInputChange}
                  placeholder={
                    formData.parent_dataset_id === 'multiple'
                      ? '< Multiple >'
                      : 'Select parent dataset...'
                  }
                  disabled={loading || (batchEdit && !batchEditItems.batch_parent_dataset_id)}
                  isNullable={true}
                  isOptional={true}
                />
              </div>
            )}

            <div className="form-block vertical">
              {batchEdit && (
                <label>
                  <Checkbox
                    checked={batchEditItems.batch_owner_principal_id}
                    name="batch_owner_principal_id"
                    label="Edit Owner"
                    onChange={e => handleInputChange(e, 'batch')}
                    disabled={loading}
                  />
                </label>
              )}

              <SelectOwner
                name="owner_principal_id"
                value={
                  formData.owner_principal_id === 'multiple'
                    ? undefined
                    : formData.owner_principal_id
                }
                label={!batchEdit}
                placeholder={formData.owner_principal_id === 'multiple' ? '< Multiple >' : ''}
                onChange={handleInputChange}
                disabled={loading || (batchEdit && !batchEditItems.batch_owner_principal_id)}
              />
            </div>
          </div>

          {hasCustomProperties && (
            <div label="More">
              <CustomPropertiesForm
                customProperties={customProperties}
                formData={formCustomPropertyValues}
                batchEdit={batchEditFormCustomPropertyValues}
                batchEditItems={batchEditItemsFormCustomPropertyValues}
                onChange={setFormCustomPropertyValues}
                loading={loading}
              />
            </div>
          )}
        </FormTabs>
      </form>
    </div>
  );
};

export const AddDatasetForm = forwardRef(_AddDatasetForm);
